import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { bridgeBars, humanEUR } from "./format";

// Drill-down helpers: strip the internal "ai:" prefix, and explain each source's contribution in
// scale-free terms (a % rate/volume change, which reads regardless of tiny per-unit prices).
const cleanKey = (k) => String(k || "").replace(/^ai:/, "");
const signedPct = (frac) => `${frac >= 0 ? "+" : ""}${Math.round(frac * 100)}%`;
function srcContext(key, s) {
  if (key === "price") {
    return s.unitPrior > 0 ? `unit cost ${signedPct(s.unitCurr / s.unitPrior - 1)}` : "unit cost changed";
  }
  if (key === "ai_volume" || key === "usage_growth") {
    return s.volPrior > 0 ? `volume ${signedPct(s.volCurr / s.volPrior - 1)}` : "new volume";
  }
  return (s.costPrior || 0) > 0 ? `${humanEUR(s.costPrior)} → ${humanEUR(s.costCurr)}` : "new source — no usage driver";
}

// SourceList is the expanded per-source breakdown for one driver bucket.
function SourceList({ driverKey, sources }) {
  const shown = sources.slice(0, 8);
  return (
    <div className="ml-3 mt-1 space-y-1 border-l border-border pb-1 pl-3">
      {shown.map((s) => {
        const label = s.displayName || cleanKey(s.sourceKey);
        return (
          <div key={s.sourceKey} className="flex items-center justify-between gap-3 text-xs">
            <span className="min-w-0 truncate text-text-2">
              {/* Deep-link to the source's detail page when the key resolves to a configured source. */}
              {s.sourceId ? (
                <Link to={`/allocation/sources/${s.sourceId}`} className="text-primary hover:text-primary-hover hover:underline">
                  {label}
                </Link>
              ) : (
                label
              )}{" "}
              <span className="text-text-3">· {srcContext(driverKey, s)}</span>
            </span>
            <span className={`tabular-nums ${s.amount >= 0 ? "text-warning" : "text-success"}`}>
              {s.amount >= 0 ? "+" : "-"}{humanEUR(Math.abs(s.amount))}
            </span>
          </div>
        );
      })}
      {sources.length > shown.length && (
        <div className="text-xs text-text-3">+{sources.length - shown.length} more</div>
      )}
    </div>
  );
}
SourceList.propTypes = { driverKey: PropTypes.string, sources: PropTypes.array };

// Composition categories → display label + segment colour. Unknown categories fall
// back to a neutral grey so the stacked bar always renders.
const CATEGORY = {
  ai: { label: "AI", color: "hsl(var(--chart-1))" },
  usage: { label: "Usage", color: "hsl(var(--chart-teal))" },
  other: { label: "Other", color: "hsl(var(--chart-pred))" },
};
function catMeta(category) {
  return CATEGORY[category] || { label: category, color: "hsl(var(--chart-pred))" };
}

// CompositionBar renders the current-window spend makeup as a single horizontal
// stacked bar (segment width = share of total), with an inline legend below.
function CompositionBar({ composition }) {
  const rows = (composition || []).filter((c) => (c.amount || 0) > 0);
  const total = rows.reduce((s, c) => s + (c.amount || 0), 0);
  if (rows.length === 0 || total <= 0) return null;
  return (
    <div className="mt-3">
      {/* Inline height: this project overrides Tailwind's height scale, so h-* classes
          collapse to 0. Heights are set via style to guarantee the bar is visible. */}
      <div className="flex w-full overflow-hidden rounded-full bg-surface-2" style={{ height: 14 }}>
        {rows.map((c) => {
          const meta = catMeta(c.category);
          const pct = (c.amount / total) * 100;
          return (
            <div
              key={c.category}
              style={{ width: `${pct}%`, height: "100%", backgroundColor: meta.color }}
              title={`${meta.label} ${Math.round(pct)}%`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {rows.map((c) => {
          const meta = catMeta(c.category);
          const pct = Math.round((c.amount / total) * 100);
          return (
            <span key={c.category} className="flex items-center gap-1.5 text-xs text-text-2">
              <span
                className="inline-block rounded-full"
                style={{ width: 10, height: 10, backgroundColor: meta.color }}
              />
              {meta.label} {pct}%
            </span>
          );
        })}
      </div>
    </div>
  );
}
CompositionBar.propTypes = {
  composition: PropTypes.arrayOf(
    PropTypes.shape({ category: PropTypes.string, amount: PropTypes.number, pct: PropTypes.number })
  ),
};

// DriverBridge shows what's driving recent spend: a stacked composition bar (where
// the money is) plus the signed week-over-week change broken out by driver.
// Returns null when there's no bridge data yet.
const DriverBridge = ({ bridge, composition = [] }) => {
  const bars = bridgeBars(bridge);
  const hasComposition = (composition || []).some((c) => (c.amount || 0) > 0);
  const [open, setOpen] = useState(null); // expanded driver key (drill-down), or null
  if (bars.length === 0 && !hasComposition) return null;

  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4">
      <div className="text-xs uppercase tracking-wide text-text-2">
        What&apos;s driving spend — last 7 days vs prior
      </div>

      {/* Two distinct reads, labelled so they don't look contradictory: composition is "where the
          money goes now"; the waterfall below is "what changed vs last week" (a small mover can be
          a big share, and vice-versa). */}
      {hasComposition && (
        <div className="mt-3 text-[11px] font-medium uppercase tracking-wide text-text-3">
          Where it goes — this week
        </div>
      )}
      <CompositionBar composition={composition} />

      {/* What moved: signed week-over-week deltas, led by the single biggest driver so a
          unit-cost (price) increase isn't buried under a usage-heavy composition bar. */}
      {bars.length > 0 && (() => {
        const deltas = bars.filter((b) => b.kind === "delta");
        const top = deltas.reduce((best, b) => (Math.abs(b.amount) > Math.abs(best ? best.amount : 0) ? b : best), null);
        const showTop = top && Math.round(Math.abs(top.amount)) >= 1;
        return (
          <div className="mt-4 space-y-1.5 border-t border-border pt-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-text-3">
              What moved — vs prior week
            </div>
            {showTop && (
              <div className="text-sm">
                <span className="text-text-2">Biggest driver: </span>
                <span className="font-semibold text-text">{top.label}</span>{" "}
                <span className={`font-semibold tabular-nums ${top.amount >= 0 ? "text-warning" : "text-success"}`}>
                  {top.amount >= 0 ? "+" : "-"}{humanEUR(Math.abs(top.amount))}
                </span>
                {top.key === "price" && (
                  <span className="text-text-2"> — a unit-cost increase, not volume</span>
                )}
              </div>
            )}
            {bars.map((b, i) => {
              const isTop = showTop && b.kind === "delta" && b.key === top.key;
              const expandable = b.kind === "delta" && Array.isArray(b.sources) && b.sources.length > 0;
              const isOpen = open === b.key;
              const isZero = b.kind === "delta" && Math.round(b.amount) === 0;
              const labelCls = b.kind === "total" ? "font-semibold text-text" : isTop ? "font-medium text-text" : isZero ? "text-text-2" : "text-text";
              // A €0 mover isn't a warning — keep it neutral so only real movement carries colour.
              const amountCls =
                b.kind === "total"
                  ? "font-semibold tabular-nums text-text"
                  : isZero
                  ? "tabular-nums text-text-3"
                  : b.amount >= 0
                  ? `tabular-nums text-warning${isTop ? " font-semibold" : ""}`
                  : `tabular-nums text-success${isTop ? " font-semibold" : ""}`;
              const RowTag = expandable ? "button" : "div";
              return (
                <React.Fragment key={i}>
                  <RowTag
                    {...(expandable ? { type: "button", onClick: () => setOpen(isOpen ? null : b.key), "aria-expanded": isOpen } : {})}
                    // width:100% inline because w-full is a no-op here; without it the <button> rows
                    // shrink to content and justify-between can't right-align the amount.
                    style={{ width: "100%" }}
                    className={`flex items-center justify-between text-left text-sm ${expandable ? "cursor-pointer hover:text-text" : ""}`}
                  >
                    <span className={labelCls}>
                      {expandable && <span className="mr-1 text-text-3" aria-hidden="true">{isOpen ? "▾" : "▸"}</span>}
                      {b.label}
                      {b.kind === "delta" && b.key === "other" && (
                        <span
                          className="ml-1 cursor-help text-text-3"
                          data-tooltip-id="instantTooltip"
                          data-tooltip-content="Spend with no usage driver mapped — flat fees, and brand-new sources, which can't be split into volume vs price yet. Map a usage driver to these sources to decompose them."
                          aria-label="What is Other?"
                        >
                          ⓘ
                        </span>
                      )}
                    </span>
                    <span className={amountCls}>
                      {b.kind === "total"
                        ? humanEUR(b.value)
                        : isZero
                        ? humanEUR(0)
                        : `${b.amount >= 0 ? "+" : "-"}${humanEUR(Math.abs(b.amount))}`}
                    </span>
                  </RowTag>
                  {expandable && isOpen && <SourceList driverKey={b.key} sources={b.sources} />}
                </React.Fragment>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
};

DriverBridge.propTypes = {
  bridge: PropTypes.shape({
    prior_actual: PropTypes.number,
    current_actual: PropTypes.number,
    deltas: PropTypes.arrayOf(
      PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, amount: PropTypes.number })
    ),
  }),
  composition: PropTypes.arrayOf(
    PropTypes.shape({ category: PropTypes.string, amount: PropTypes.number, pct: PropTypes.number })
  ),
};

export default DriverBridge;
